/**
 * Handle address validation results and prompt user to use found alternatives
 * 
 * Workflow:
 * 1. Show validation status alert
 * 2. If partial street match found, prompt to use it
 * 3. If different zipcode found, prompt to use it
 * 
 * @param validation - Validation results from address verification service
 * @param addr - Address object to update if user accepts suggestions
 */
/* eslint-disable no-console */
import { Component, Injector } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ViewBasePage } from '../viewBase/viewBase.page';
import type { TResponse } from '@shared/models';
interface TValidation {
    statusTitle: string;
    statusMessage: string;
    streetNamePartialMatch: boolean;
    foundAddress: string;
    zipcodePartialMatch: boolean;
    foundZipcode: string;
}
interface Address {
    streetAddress?: string;
    zipcode?: string;
}

@Component({
    'selector': 'app-addressBase',
    'templateUrl': './addressBase.page.html',
    'styleUrls': ['./addressBase.page.scss'],
    'standalone': true,
    'imports': [IonicModule, CommonModule, FormsModule]

})
export class AddressBasePage extends ViewBasePage {
    constructor(injector: Injector) {
        super(injector);
    }

    protected async potentiallyUseFoundInfo(validation: TValidation, addr: Address): Promise<void> {
        try {
            // Show initial validation status
            await this.showValidationStatus(validation);

            // Handle partial street address match
            if (validation.streetNamePartialMatch) {
                await this.promptForPartialAddressMatch(validation.foundAddress, addr);
            }

            // Handle zipcode mismatch
            if (validation.zipcodePartialMatch) {
                await this.promptForZipcodeMatch(validation.foundZipcode, addr);
            }
        } catch (error) {
            console.error('Error handling address validation:', error);
            await this.uxHelperService.showAlert('Error', 'An error occurred while processing the address validation.'            );
        }
    }


    // protected async potentiallyUseFoundInfo(
    //     validation: TValidation,
    //     addr: Address
    // ): Promise<void> {
    //     // Show initial validation status
    //     await this.showValidationStatus(validation);

    //     // Handle partial street address match
    //     if (validation.streetNamePartialMatch) {
    //         await this.promptForPartialAddressMatch(validation.foundAddress, addr);
    //     }

    //     // Handle zipcode mismatch
    //     if (validation.zipcodePartialMatch) {
    //         await this.promptForZipcodeMatch(validation.foundZipcode, addr);
    //     }
    // }

    /**
     * Show the initial validation status alert
     */
    private async showValidationStatus(validation: TValidation): Promise<void> {
        await this.uxHelperService.showAlert(validation.statusTitle, validation.statusMessage);
    }

    /**
     * Prompt user to accept a partial street address match
     * 
     * @param foundAddress - The suggested address found by verification
     * @param addr - Address object to update if user accepts
     */
    private async promptForPartialAddressMatch(foundAddress: string, addr: Address): Promise<void> {
        const title = 'Partial Match Found';
        const message = `A nearby address was found:\n\n${foundAddress}\n\nWould you like to use this address?`;
        const response = await this.uxHelperService.showAlert(title, message);

        if (response.okay) {
            addr.streetAddress = foundAddress;
            console.log('✅ User accepted partial address match:', foundAddress);
        } else {
            console.log('❌ User declined partial address match');
        }
    }

    /**
     * Prompt user to accept a different zipcode
     * 
     * @param foundZipcode - The suggested zipcode found by verification
     * @param addr - Address object to update if user accepts
     */
    private async promptForZipcodeMatch(foundZipcode: string, addr: Address): Promise<void> {
        const title = 'Different Zipcode Found';
        const message = `The address was found in a different zipcode:\n\n${foundZipcode}\n\nWould you like to use this zipcode?`;
        const response = await this.uxHelperService.showAlert(title, message);

        if (response.okay) {
            addr.zipcode = foundZipcode;
            console.log('✅ User accepted zipcode change:', foundZipcode);
        } else {
            console.log('❌ User declined zipcode change');
        }
    }

    /**
     * Show a confirmation dialog and return the user's response
     * 
     * @param title - Dialog title
     * @param message - Dialog message
     * @returns Promise resolving to user's response
     */
    // private async showConfirmationDialog(title: string, message: string): Promise<TResponse> {
    // return new Promise<TResponse>((resolve) => {
    //     this.uxHelperService.showConfirmationAlert(
    //         title,
    //         message,
    //         (response: TResponse) => {
    //             resolve(response);
    //         }
    //     );
    // });
    // const response = await this.uxHelperService.showAlert(title, message);
    // return response;
}

// protected async potentiallyUseFoundInfo(validation: TValidation, addr: Address) {
//     let resp: TResponse;

//     await this.uxHelperService.showAlert(validation.statusTitle, validation.statusMessage, async () => {
//         if (validation.streetNamePartialMatch) {
//             const title = 'However, a nearby partial match was found. Do you want to use this address?';
//             await this.uxHelperService.showConfirmationAlert(title, validation.foundAddress, async (response: TResponse) => {
//                 if (response.okay) {
//                     // this.vm.ticket.dropoff.streetAddress = validation.foundAddress;
//                     addr.streetAddress = validation.foundAddress;
//                 }
//             });
//         }
//         if (validation.zipcodePartialMatch) {
//             const title = 'However, the address was found in a different zipcode. Do you want to use this zipcode?';
//             await this.uxHelperService.showConfirmationAlert(title, validation.foundZipcode, async (response: TResponse) => {
//                 if (response.okay) {
//                     // this.vm.ticket.dropoff.zipcode = validation.foundZipcode;
//                     addr.zipcode = validation.foundZipcode;
//                 }
//             });
//         }
//     });
// }
